import { setCurrentEventContext } from "@features/access/eventContextService";
import { clearPendingEventIdStorage, readPendingEventId } from "@features/access/pendingEventStorage";
import { useAuthStore } from "@features/authentication/stores/authStore";
import logger from "@logger";
import useCurrentUser from "@shared/queries/useCurrentUser";
import QUERY_KEYS from "@shared/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Applies a pending event id from session storage after auth/onboarding when the user doc exists.
 */
export function useApplyPendingEventContext(): void {
  const { status, userId } = useAuthStore();
  const { user, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const applyInFlight = useRef(false);

  useEffect(() => {
    if (status !== "authenticated" || !userId || isLoading || !user) {
      return;
    }

    const pendingEventId = readPendingEventId();
    if (!pendingEventId) {
      return;
    }

    // Even when the pending event matches the current one, re-apply so
    // lastKnownEventEnteredAt is refreshed (drives presence badges and order).

    if (applyInFlight.current) {
      return;
    }

    applyInFlight.current = true;
    let cancelled = false;

    void (async () => {
      try {
        await setCurrentEventContext(pendingEventId);
        if (!cancelled) {
          clearPendingEventIdStorage();
          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] });
        }
      } catch (error) {
        logger.error("Failed to apply pending event context:", error);
        if (error instanceof Error && error.message.includes("permission denied")) {
          clearPendingEventIdStorage();
        }
      } finally {
        applyInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, userId, user, isLoading, queryClient]);
}
